// --- React & Redux
import React, {
  useEffect,
  useCallback,
  useState,
  useRef,
  useMemo,
} from "react";
import { useSelector } from "react-redux";
import { useSearchParams, useNavigate, useLocation } from "react-router-dom";
// Test SSH authentication
// --- Actions & Hooks
import { useOffersActions } from "../../store/offers/useOffers";
import { useAsyncEffect } from "../../utils/hooks/useAsyncEffect";
import { useLoading } from "../../utils/hooks/useLoading";
import { useToasts } from "../../utils/hooks/useToasts";
import { useUploadDialog } from "../../components/dialogs/dialogs/upload";
import { useToastNotification } from "../../components/ToastNotifications";
// --- External UI Libraries
import { Col, Row } from "antd";
import Button from "@dtci/spellbook/Button";
import Typography from "@dtci/spellbook/Typography";
// --- Components
import TwoRowsLayout from "./two-row";
import { OffersSearchForm } from "./components/search-form";
import { OffersGrid } from "./components/grid";
import { FiltersSidebar } from "./components/filters-sidebar";
import { ColumnsDrawer } from "./components/drawers/columns";
import { OffersUploadTasksDrawer } from "./components/drawers/upload-tasks";
import { PaginationBar } from "../../components/PaginationBar";
import ToastNotificationProvider from "../../components/ToastNotifications";
// --- Utilities
import { tapApi } from "../../api";
import { validateFiles } from "../../utils/fileValidation";
import { startJobStatusTracker } from "../../utils/jobStatusTracker";
import { parseSearchParams, updateSearchParams } from "../../utils/urlParams";
import { useStoredItemsPerPageSync } from "tap/utils/hooks/useStoredItemsPerPageSync.js";
import { PAGINATION_STORAGE_KEYS } from "tap/utils/paginationPreferences.js";
import { hasFeature } from "tap/utils/permissions.js";
import {
  buildOffersFilterOptions,
  getOffersFilterColumns,
} from "tap/pages/Offers/utils/offersFilterOptions.js";
// --- Styles
import "./styles.scss";

/** @returns {string|number|null} */
function getJobIdFromUploadResponse(data) {
  if (data == null) return null;
  if (typeof data === "number" || typeof data === "string") {
    return data;
  }
  if (typeof data === "object" && data !== null) {
    if ("error" in data && data.error) return null;
    if (data.id != null) return data.id;
    if (data.jobId != null) return data.jobId;
  }
  return null;
}

/**
 * Offers Page
 *
 * Features:
 * - Displays offers in a grid with filtering, sorting, and search
 * - Client-side pagination with URL state preservation
 * - Persistent filter sidebar on the left
 * - Supports editing, pushing to RL, and deleting offers
 * - Can be accessed directly via URL with jobId parameter
 * - Search form for filtering offers
 *
 * @returns {Element}
 * @constructor
 */
export const OffersPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const jobId = searchParams.get("jobId");

  const { setLoadingTrue, setLoadingFalse } = useLoading();
  const { showErrorToast } = useToasts();
  const { openUploadDialog } = useUploadDialog();
  const { createJobProgressToastHandler } = useToastNotification();
  const gridRef = useRef(null);

  const {
    offers,
    selectedJob,
    hiddenColumns,
    searchAll,
    internalFilters,
    mediaOptions,
    territoryOptions,
    languageOptions,
    formatOptions,
    currencyOptions,
    transactionalWindowOptions,
    offerSubDubLanguageOptions,
    itemsPerPage,
    pageSizeOptions,
  } = useSelector((state) => state.offers);

  const {
    updateOffers,
    clearOffersFilters,
    loadOffersFilters,
    setOffersFilters,
  } = useOffersActions();

  const hasEpisodeFeature = hasFeature(
    "TAP_OFFER_EPISODE_TIER_AND_EPISODE_EMA_PRICE_TYPE",
  );

  const effectiveJobId = jobId || selectedJob?.id;

  const filterColumns = useMemo(
    () => getOffersFilterColumns(hasEpisodeFeature),
    [hasEpisodeFeature],
  );

  const filterOptions = useMemo(
    () => buildOffersFilterOptions(offers, filterColumns),
    [offers, filterColumns],
  );

  const activeFilterCount = useMemo(
    () =>
      Object.values(internalFilters).filter((v) => v && v.length > 0).length,
    [internalFilters],
  );

  const [columnsDrawerOpen, setColumnsDrawerOpen] = useState(false);

  // Read current page from URL (1-based) and convert to 0-based index
  const urlParams = parseSearchParams(location.search);
  const currentPage = urlParams.page
    ? Math.max(0, parseInt(urlParams.page, 10) - 1)
    : 0;
  const currentPageSize = urlParams.pageSize
    ? parseInt(urlParams.pageSize, 10)
    : itemsPerPage;

  useStoredItemsPerPageSync({
    storageKey: PAGINATION_STORAGE_KEYS.offers,
    itemsPerPage,
    onSync: updateOffers,
    fallback: 10,
  });

  useEffect(() => {
    if (jobId) {
      loadOffersFilters(jobId);
    }
  }, [jobId, loadOffersFilters]);

  // Update Redux itemsPerPage if URL specifies different value
  useEffect(() => {
    if (currentPageSize !== itemsPerPage) {
      updateOffers({ itemsPerPage: currentPageSize });
    }
  }, [currentPageSize, itemsPerPage, updateOffers]);

  // Lazily load reference data once on mount if not already present
  useAsyncEffect(
    async ({ isMounted }) => {
      // Check if reference data already loaded
      const hasReferenceData =
        mediaOptions?.length > 0 &&
        territoryOptions?.length > 0 &&
        languageOptions?.length > 0 &&
        formatOptions?.length > 0 &&
        currencyOptions?.length > 0 &&
        transactionalWindowOptions?.length > 0;

      if (hasReferenceData) return;

      try {
        const [
          medias,
          territories,
          languages,
          subDubLanguages,
          formats,
          currencies,
          transactionalWindows,
        ] = await Promise.all([
          tapApi.mediaAndTerritories("media"),
          tapApi.mediaAndTerritories("territory"),
          tapApi.mediaAndTerritories("language"),
          tapApi.mediaAndTerritories("subDublanguage"),
          tapApi.format(),
          tapApi.currency(),
          tapApi.transactionalWindows(),
        ]);

        if (isMounted()) {
          updateOffers({
            mediaOptions: Array.isArray(medias) ? medias : [],
            territoryOptions: Array.isArray(territories) ? territories : [],
            languageOptions: Array.isArray(languages) ? languages : [],
            offerSubDubLanguageOptions: Array.isArray(subDubLanguages)
              ? subDubLanguages
              : [],
            formatOptions: Array.isArray(formats) ? formats : [],
            currencyOptions: Array.isArray(currencies) ? currencies : [],
            transactionalWindowOptions: Array.isArray(transactionalWindows)
              ? transactionalWindows
              : [],
          });
        }
      } catch (error) {
        console.error("Failed to load reference data:", error);
      }
    },
    [], // ✅ Empty deps - only run once on mount
  );

  const toggleColumnVisibility = useCallback(
    (field) => {
      const newHiddenColumns = hiddenColumns.includes(field)
        ? hiddenColumns.filter((f) => f !== field)
        : [...hiddenColumns, field];
      updateOffers({ hiddenColumns: newHiddenColumns });
    },
    [hiddenColumns, updateOffers],
  );

  useAsyncEffect(
    async ({ isMounted }) => {
      if (!jobId) return;

      // If offers already loaded for this job (from LicensingJobs navigation), just dismiss loading
      const alreadyLoaded =
        selectedJob?.id === Number(jobId) && offers && offers.length > 0;

      if (alreadyLoaded) {
        setLoadingFalse();
        return;
      }

      // Otherwise fetch data (direct URL navigation)
      setLoadingTrue();
      try {
        const jobData = await tapApi.licensingJobById(jobId);
        if (
          isMounted() &&
          jobData &&
          !jobData.code &&
          !jobData.httpCode &&
          !jobData.error
        ) {
          updateOffers({ selectedJob: jobData });
        }

        const offersData = await tapApi.licensingJobOfferTable(jobId);
        if (
          isMounted() &&
          offersData &&
          !offersData.code &&
          !offersData.httpCode &&
          !offersData.error
        ) {
          updateOffers({ offers: offersData });
        }
      } catch (error) {
        console.error("Failed to fetch offers:", error);
      } finally {
        if (isMounted()) {
          setLoadingFalse();
        }
      }
    },
    [jobId],
  );

  const handleRefresh = () => {
    const id = jobId || selectedJob?.id;
    if (!id) return;
    updateOffers({ refreshOffers: true });
  };

  const handleSearchChange = (value) => {
    updateOffers({ searchAll: value });
  };

  const handleFilterChange = useCallback(
    (field, selectedOptions) => {
      const newFilters = {
        ...internalFilters,
        [field]:
          selectedOptions && selectedOptions.length > 0 ? selectedOptions : [],
      };
      setOffersFilters(newFilters, effectiveJobId);
    },
    [internalFilters, effectiveJobId, setOffersFilters],
  );

  const handleClearFilters = () => {
    clearOffersFilters(effectiveJobId);
  };

  const handlePageChange = (pageNumber) => {
    const params = parseSearchParams(location.search);
    updateSearchParams(location, navigate, {
      ...params,
      page: pageNumber + 1, // Convert 0-based index to 1-based URL parameter
    });
  };

  const handlePageSizeChange = (newSize) => {
    const params = parseSearchParams(location.search);
    updateSearchParams(location, navigate, {
      ...params,
      pageSize: newSize,
      page: 1, // Reset to first page when size changes
    });
  };

  const isTprJob = !!selectedJob?.isTpr;

  const onOpenUploadDialog = () => {
    if (isTprJob) {
      showErrorToast("Offer upload is not available for TPR licensing jobs.");
      return;
    }
    openUploadDialog(
      {},
      async ({ file }) => {
        if (!file) return;

        const files = Array.isArray(file) ? file : [file];
        const validation = validateFiles(files);
        if (!validation.valid) {
          showErrorToast(validation.error);
          return;
        }

        const formData = new FormData();
        files.forEach((f) => formData.append("file", f));

        try {
          setLoadingTrue();
          const response = await tapApi.uploadOffers(formData);

          if (response?.code || response?.httpCode || response?.error) {
            showErrorToast(
              response?.message ||
                (typeof response?.error === "string"
                  ? response.error
                  : "Upload failed."),
            );
            setLoadingFalse();
            return;
          }

          const trackerJobId = getJobIdFromUploadResponse(response);
          if (!trackerJobId) {
            showErrorToast("Upload did not return a job id.");
            setLoadingFalse();
            return;
          }

          const onJobProgress = createJobProgressToastHandler(trackerJobId);

          startJobStatusTracker(
            trackerJobId,
            async (message, status) => {
              onJobProgress(message, status);
              if (status === "success") {
                updateOffers({ refreshOffers: true });
              }
              setLoadingFalse();
            },
            (jobTasks, jobInfo) => {
              if (
                jobInfo?.jobStatus === "COMPLETE" &&
                (jobInfo?.errorCount ?? 0) === 0
              ) {
                return;
              }
              updateOffers({
                isOffersUploadTasksDrawerOpen: true,
                offersUploadJobTasks: jobTasks || [],
              });
            },
          );

          setLoadingFalse();
        } catch (error) {
          console.error("Error uploading offers:", error);
          showErrorToast(error?.message || "Error uploading offers.");
          setLoadingFalse();
        }
      },
      "offers",
    );
  };

  const jobDisplay = selectedJob?.id
    ? `Job ${selectedJob.id}`
    : jobId
      ? `Job ${jobId}`
      : "";

  // Apply filters to calculate filtered offers (moved from grid for better reactivity)
  const filteredOffers = useMemo(() => {
    if (!offers || !Array.isArray(offers)) return [];

    // Apply dropdown filters
    const activeFilters = Object.entries(internalFilters).filter(
      ([, values]) => values && values.length > 0,
    );

    let result = offers;

    if (activeFilters.length > 0) {
      result = offers.filter((offer) => {
        return activeFilters.every(([field, selectedOptions]) => {
          const offerValue = offer[field];
          if (offerValue === null || offerValue === undefined) return false;
          return selectedOptions.some(
            (option) => String(offerValue) === String(option.value),
          );
        });
      });
    }

    // Apply text search
    if (searchAll && searchAll.trim() !== "") {
      const lowerSearch = searchAll.toLowerCase().trim();
      result = result.filter((offer) => {
        return Object.values(offer).some((value) => {
          if (value === null || value === undefined) return false;
          return String(value).toLowerCase().includes(lowerSearch);
        });
      });
    }

    return result;
  }, [offers, internalFilters, searchAll]);

  // Calculate total items and pages based on filtered data
  const totalItems = filteredOffers.length;
  const totalPages = Math.ceil(totalItems / currentPageSize) || 0;

  // Calculate paginated slice of offers
  const startIndex = currentPage * currentPageSize;
  const endIndex = startIndex + currentPageSize;
  const paginatedOffers = useMemo(() => {
    return filteredOffers.slice(startIndex, endIndex);
  }, [filteredOffers, startIndex, endIndex]);

  return (
    <>
      <Row className="offers-page-header">
        <Col className="offers-page-title-col">
          <Button
            type="button"
            styleType="link"
            iconType="fa-solid fa-arrow-left"
            onClick={() => navigate(-1)}
            className="offers-back-button"
          >
            Back
          </Button>
          <Typography variant="T50" className="offers-page-title">
            Offers {jobDisplay && `- ${jobDisplay}`}
          </Typography>
        </Col>
        <Col className="offers-page-actions">
          {!isTprJob && (
            <Button
              type="button"
              styleType="link"
              iconType="fa-solid fa-upload"
              onClick={onOpenUploadDialog}
            >
              Upload
            </Button>
          )}
          <Button
            type="button"
            styleType="link"
            iconType="fa-regular fa-table-columns"
            onClick={() => setColumnsDrawerOpen(true)}
          >
            Columns
          </Button>
          <Button
            type="button"
            styleType="link"
            iconType="fa-regular fa-download"
            onClick={() => gridRef.current?.handleExport()}
          >
            Download
          </Button>
          <Button
            type="button"
            styleType="link"
            iconType="fa-regular fa-print"
            onClick={() => gridRef.current?.handlePrint()}
          >
            Print
          </Button>
          <Button
            iconType="fa-solid fa-rotate"
            labelText="Refresh"
            styleType="link"
            onClick={handleRefresh}
            disabled={!jobId && !selectedJob?.id}
          >
            Refresh
          </Button>
        </Col>
      </Row>

      <div className="offers-page-content">
        <TwoRowsLayout>
          <main data-position="top">
            <OffersSearchForm
              searchAll={searchAll}
              onSearchChange={handleSearchChange}
            />
          </main>
          <main data-position="bottom">
            <OffersGrid
              ref={gridRef}
              searchAll={searchAll}
              hiddenColumns={hiddenColumns}
              paginatedOffers={paginatedOffers}
              filteredOffers={filteredOffers}
              currentPage={currentPage}
            />
          </main>
        </TwoRowsLayout>

        <FiltersSidebar
          filterColumns={filterColumns}
          filterOptions={filterOptions}
          internalFilters={internalFilters}
          onFilterChange={handleFilterChange}
          onClearFilters={handleClearFilters}
          activeFilterCount={activeFilterCount}
        />
      </div>

      <ColumnsDrawer
        open={columnsDrawerOpen}
        onClose={() => setColumnsDrawerOpen(false)}
        columnOptions={gridRef.current?.COLUMN_OPTIONS}
        hiddenColumns={hiddenColumns}
        onToggleColumn={toggleColumnVisibility}
      />

      <OffersUploadTasksDrawer onOpenUploadDialog={onOpenUploadDialog} />

      <PaginationBar
        totalPages={totalPages}
        currentPage={currentPage}
        totalItems={totalItems}
        itemsPerPage={currentPageSize}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        pageSizeOptions={pageSizeOptions}
      />

      <ToastNotificationProvider />
    </>
  );
};
